import { HOST_VIDEO_API } from 'config';
import { rtkApi } from 'redux/rtkApi';
import { ListParams } from 'types/common';
import {
  IMarathonReview,
  IMarathonReviewBanRequest,
  IMarathonReviewComplaint,
  IMarathonReviewComplaintListResponse,
  IMarathonReviewListResponse,
  IMarathonReviewSortRequest,
  IMarathonReviewUpdateRequest,
  MarathonReviewStatus,
} from 'types/marathonReview';

export type MarathonReviewsListParams = ListParams & {
  id: number | string;
  scan?: string;
  status?: MarathonReviewStatus[];
};

export type MarathonReviewsGlobalListParams = ListParams & {
  scan?: string;
  status?: MarathonReviewStatus[];
};

export type MarathonReviewComplaintsListParams = {
  user_id?: 'y' | 'n';
  page?: number;
  per_page?: number;
};

const marathonReviews = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getAllMarathonReviews: build.query<
      IMarathonReviewListResponse,
      MarathonReviewsGlobalListParams
    >({
      query: (params) => ({
        url: `${HOST_VIDEO_API}admin/reviews`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: IMarathonReviewListResponse) => response,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((r) => ({ type: 'MarathonReviews' as const, id: r.id })),
              { type: 'MarathonReviews', id: 'LIST' },
            ]
          : [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    getMarathonReviewsList: build.query<IMarathonReviewListResponse, MarathonReviewsListParams>({
      query: ({ id, ...params }) => ({
        url: `${HOST_VIDEO_API}admin/marathon-templates/${id}/reviews`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: IMarathonReviewListResponse) => response,

      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((r) => ({ type: 'MarathonReviews' as const, id: r.id })),
              { type: 'MarathonReviews', id: 'LIST' },
            ]
          : [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    updateMarathonReview: build.mutation<
      IMarathonReview,
      { id: number; data: IMarathonReviewUpdateRequest }
    >({
      query: ({ id, data }) => ({
        url: `${HOST_VIDEO_API}admin/reviews/${id}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: { data: IMarathonReview }) => response.data,
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'MarathonReviews', id: result.id },
              { type: 'MarathonReviews', id: 'LIST' },
            ]
          : [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    deleteMarathonReview: build.mutation<void, number>({
      query: (id) => ({
        url: `${HOST_VIDEO_API}admin/reviews/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'MarathonReviews', id }],
    }),

    banMarathonReview: build.mutation<
      IMarathonReview,
      { id: number; data: IMarathonReviewBanRequest }
    >({
      query: ({ id, data }) => ({
        url: `${HOST_VIDEO_API}admin/reviews/${id}/ban`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { data: IMarathonReview }) => response.data,
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'MarathonReviews', id: result.id },
              { type: 'MarathonReviews', id: 'LIST' },
            ]
          : [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    patchMarathonReview: build.mutation<IMarathonReview, { id: number }>({
      query: ({ id }) => ({
        url: `${HOST_VIDEO_API}admin/reviews/${id}/toggle-fixed`,
        method: 'PATCH',
      }),
      transformResponse: (response: { data: IMarathonReview }) => response.data,
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'MarathonReviews', id: result.id },
              { type: 'MarathonReviews', id: 'LIST' },
            ]
          : [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    getMarathonReview: build.query<IMarathonReview, number>({
      query: (id) => ({
        url: `${HOST_VIDEO_API}admin/reviews/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IMarathonReview }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'MarathonReviews', id }],
    }),

    sortMarathonReviews: build.mutation<
      IMarathonReviewListResponse,
      { id: number; data: IMarathonReviewSortRequest }
    >({
      query: ({ id, data }) => ({
        url: `${HOST_VIDEO_API}admin/marathon-templates/${id}/reviews/sort`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: [{ type: 'MarathonReviews', id: 'LIST' }],
    }),

    getMarathonReviewComplaints: build.query<
      IMarathonReviewComplaintListResponse,
      MarathonReviewComplaintsListParams
    >({
      query: (params) => ({
        url: `${HOST_VIDEO_API}admin/reviews-complaints`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: IMarathonReviewComplaintListResponse) => response,
      providesTags: [{ type: 'MarathonReviewComplaints', id: 'LIST' }],
    }),

    processMarathonReviewComplaint: build.mutation<IMarathonReviewComplaint, number>({
      query: (id) => ({
        url: `${HOST_VIDEO_API}admin/reviews-complaints/${id}`,
        method: 'PUT',
      }),
      transformResponse: (response: { data: IMarathonReviewComplaint }) => response.data,
      invalidatesTags: [{ type: 'MarathonReviewComplaints', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAllMarathonReviewsQuery,
  useGetMarathonReviewsListQuery,
  useGetMarathonReviewQuery,
  useUpdateMarathonReviewMutation,
  useDeleteMarathonReviewMutation,
  useBanMarathonReviewMutation,
  usePatchMarathonReviewMutation,
  useSortMarathonReviewsMutation,
  useGetMarathonReviewComplaintsQuery,
  useProcessMarathonReviewComplaintMutation,
} = marathonReviews;
